import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import RadiologyIcon from '@mui/icons-material/AirlineSeatReclineExtra';
import CloseIcon from '@mui/icons-material/Close';
import MiniSTLViewer, { CombinedMiniSTLViewer } from './MiniSTLViewer';

const thumbSx = {
    width: 64, height: 64, borderRadius: '2px', overflow: 'hidden',
    bgcolor: 'var(--bg-card)', border: '2px solid transparent',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:hover': { borderColor: '#c9a0dc', boxShadow: '0 0 0 2px rgba(201,160,220,0.4)' },
};

const itemWrapSx = {
    position: 'relative', flexShrink: 0,
    transition: 'opacity 0.2s, border-color 0.2s, transform 0.2s',
    opacity: 0.7,
    '&:hover': { opacity: 1, transform: 'scale(1.04)' },
    '&:hover .gallery-delete': { opacity: 1 },
};

const GalleryStrip = ({ files, onThumbClick, onPairClick, onRemoveFile }) => {
    const preOpMaxilla = files.find(pf => pf.category === 'PreOp Maxilla');
    const preOpMandible = files.find(pf => pf.category === 'PreOp Mandible');
    const postOpMaxilla = files.find(pf => pf.category === 'PostOp Maxilla');
    const postOpMandible = files.find(pf => pf.category === 'PostOp Mandible');

    const renderedPairs = new Set();

    const pairFirstIndex = (a, b) => {
        const ia = a ? files.indexOf(a) : Infinity;
        const ib = b ? files.indexOf(b) : Infinity;
        return Math.min(ia, ib);
    };
    const preOpFirst = pairFirstIndex(preOpMaxilla, preOpMandible);
    const postOpFirst = pairFirstIndex(postOpMaxilla, postOpMandible);

    const renderSingleThumb = (pf, index) => (
        <Box key={`${pf.file.name}-${index}`} sx={itemWrapSx}>
            <Box sx={{ ...thumbSx, cursor: 'pointer' }} onClick={() => onThumbClick(pf)}>
                {pf.isImage ? (
                    <img src={pf.preview} alt={pf.file.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {pf.isCBCT ? <RadiologyIcon sx={{ color: '#64b5f6', fontSize: 28 }} />
                            : pf.is3D ? <ViewInArIcon sx={{ color: '#c9a0dc', fontSize: 28 }} />
                            : <InsertDriveFileIcon sx={{ color: '#c9a0dc', fontSize: 28 }} />}
                    </Box>
                )}
            </Box>
            <Typography variant="caption" sx={{
                display: 'block', mt: 0.5, fontSize: 10, fontWeight: 500,
                color: 'var(--text-3)', maxWidth: 64, textAlign: 'center',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{pf.category || pf.file.name}</Typography>
            <IconButton className="gallery-delete" size="small"
                onClick={() => onRemoveFile(files.indexOf(pf))}
                sx={{
                    position: 'absolute', top: -6, right: -6, bgcolor: '#f44336', color: '#fff',
                    width: 18, height: 18, opacity: 0, transition: 'opacity 0.15s ease',
                    '&:hover': { bgcolor: '#d32f2f' },
                }}>
                <CloseIcon sx={{ fontSize: 10 }} />
            </IconButton>
        </Box>
    );

    const renderGroupedPair = (label, upper, lower, color) => (
        <Box key={label} sx={{ ...itemWrapSx, opacity: 1 }}>
            <Box sx={{
                position: 'relative', borderRadius: 1, overflow: 'hidden',
                bgcolor: 'var(--bg-card)', border: '2px solid transparent',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': { borderColor: color, boxShadow: `0 0 0 2px ${color}40` },
                '&:hover .model-delete': { opacity: 1 },
                cursor: 'pointer',
            }}
                onClick={() => (upper || lower) && onPairClick(upper, lower, label)}
            >
                {(upper || lower) ? (
                    <CombinedMiniSTLViewer upper={upper?.file} lower={lower?.file} width={120} height={120} />
                ) : (
                    <Box sx={{ width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--bg-surface)' }}>
                        <ViewInArIcon sx={{ fontSize: 28, color: 'var(--text-3)' }} />
                    </Box>
                )}
                {/* Jaw labels */}
                <Box sx={{ position: 'absolute', top: 4, left: 4, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    {upper && (
                        <Typography sx={{
                            fontSize: 7, fontWeight: 700, color: '#d4c4a0', bgcolor: 'rgba(0,0,0,0.6)',
                            px: 0.5, borderRadius: 0.5, lineHeight: 1.4,
                        }}>Mx</Typography>
                    )}
                    {lower && (
                        <Typography sx={{
                            fontSize: 7, fontWeight: 700, color: '#b0a090', bgcolor: 'rgba(0,0,0,0.6)',
                            px: 0.5, borderRadius: 0.5, lineHeight: 1.4,
                        }}>Md</Typography>
                    )}
                </Box>
                {/* Delete buttons */}
                <Box className="model-delete" sx={{
                    position: 'absolute', top: 3, right: 3, display: 'flex', flexDirection: 'column', gap: 0.3,
                    opacity: 0, transition: 'opacity 0.15s ease',
                }}>
                    {upper && (
                        <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); onRemoveFile(files.indexOf(upper)); }}
                            sx={{ bgcolor: '#f44336', color: '#fff', width: 14, height: 14, '&:hover': { bgcolor: '#d32f2f' } }}>
                            <CloseIcon sx={{ fontSize: 8 }} />
                        </IconButton>
                    )}
                    {lower && (
                        <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); onRemoveFile(files.indexOf(lower)); }}
                            sx={{ bgcolor: '#f44336', color: '#fff', width: 14, height: 14, '&:hover': { bgcolor: '#d32f2f' } }}>
                            <CloseIcon sx={{ fontSize: 8 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <Typography variant="caption" sx={{
                display: 'block', mt: 0.5, fontSize: 10, fontWeight: 600,
                color, textAlign: 'center',
            }}>{label}</Typography>
        </Box>
    );

    return (
        <Box sx={{
            position: 'fixed', bottom: 0, left: 'var(--drawer-width, 240px)', right: 0, zIndex: 1200,
            bgcolor: 'var(--bg-surface)',
            borderTop: '1px solid var(--border)', px: 2, py: 1.5,
            display: 'flex', gap: 1.5, alignItems: 'center', overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--border-h)', borderRadius: 2 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        }}>
            {files.map((pf, i) => {
                const cat = pf.category;
                if (cat === 'PreOp Maxilla' || cat === 'PreOp Mandible') {
                    if (i === preOpFirst && !renderedPairs.has('PreOp')) {
                        renderedPairs.add('PreOp');
                        return renderGroupedPair('PreOp', preOpMaxilla, preOpMandible, '#7b68ee');
                    }
                    return null;
                }
                if (cat === 'PostOp Maxilla' || cat === 'PostOp Mandible') {
                    if (i === postOpFirst && !renderedPairs.has('PostOp')) {
                        renderedPairs.add('PostOp');
                        return renderGroupedPair('PostOp', postOpMaxilla, postOpMandible, '#c9a0dc');
                    }
                    return null;
                }
                return renderSingleThumb(pf, i);
            })}
        </Box>
    );
};

export default GalleryStrip;
